import React, { useEffect } from "react";
import {
  CreditCard,
  Repeat,
  Globe,
  ShieldCheck,
  Activity,
  Layers,
} from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import "./Babbel.scss";

const Babbel = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletListLeft = (
    <BulletList>
      <BulletItem>
        Supporting regional payment realities without fragmenting the system
      </BulletItem>
      <BulletItem>
        Preserving a simple, trustworthy user experience despite significant
        backend complexity
      </BulletItem>
      <BulletItem>
        Ensuring financial correctness, resilience, and observability in a
        domain with zero tolerance for error
      </BulletItem>
      <BulletItem>
        Designing a solution that could evolve as Babbel expanded into new
        markets and products
      </BulletItem>
    </BulletList>
  );

  const bulletListRight = (
    <BulletList>
      <BulletItem>
        Stripe was used as the external system of record for payments and
        subscription state
      </BulletItem>
      <BulletItem>
        Internal services remained authoritative for entitlements and product
        access
      </BulletItem>
      <BulletItem>
        Asynchronous events such as renewals, retries, and failures were handled
        explicitly to maintain consistency
      </BulletItem>
      <BulletItem>
        Observability and traceability were built in to support operational
        confidence and rapid diagnosis
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Babbel Subscription Payments"
        subtitle="Building a resilient payments and subscription platform supporting millions of learners worldwide"
        category="ENGINEERING"
        role="Principal Software Engineer – Payments & Subscriptions"
        year="2024 - 2025"
        platform="Web"
        technologies={[
          "AWS, Node JS, JavaScript, TypeScript, CSS, Docker, HTML, Next JS, Stripe SDK, React",
        ]}
        client="Babbel"
      />
      <VisualShowcase
        imageUrl={images.babbelApp1}
        imageAlt="Babbel App"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Orchestrating global payments at scale",
          "Babbel’s subscription business spans multiple countries, currencies, and regulatory environments. As the platform scaled, checkout could no longer be treated as a single page or isolated feature.",
          "Each market introduced different payment methods, tax rules, and user expectations, while the business required a consistent subscription model, reliable revenue capture, and a low-friction experience across devices.",
          "The core challenge was not implementing payments, but orchestrating complexity:",
        ]}
        extraChallengeContent={[
          "At this scale, checkout became a distributed system with direct impact on revenue, trust, and operational risk.",
        ]}
        extraSolutionContent={[
          "Rather than optimising for a single market or payment flow, the architecture prioritised correctness, maintainability, and extensibility. This allowed product teams to introduce new subscription tiers, upgrades, and regional payment methods without increasing payment risk or operational overhead.",
          " The result was a global checkout system that hides complexity from the learner while providing a robust foundation for Babbel’s continued growth.",
        ]}
        solutionContent={[
          "A scalable, region-aware payment system with UX at its core",
          "The result was a scalable, secure checkout system that supports Babbel’s global user base while maintaining a smooth, intuitive purchasing experience.",
          "By tightly integrating Stripe with Babbel’s internal platforms, the system reliably handles:",
        ]}
        metrics={[
          {
            value: "+30%",
            label: "Checkout conversion across key global growth markets",
          },
          {
            value: "+35%",
            label: "Mobile checkout completion (web)",
          },
          {
            value: "+50%",
            label: "Adoption of local payment methods after regional rollout",
          },
          {
            value: "+15%",
            label: "Revenue per visitor attributable to checkout improvements",
          },
          {
            value: "99.9%",
            label: "Payment success rate across primary subscription flows",
          },
        ]}
        bulletsLeft={bulletListLeft}
        bulletsRight={bulletListRight}
      />
      <VisualShowcase
        title="Checkout & Payment Experience"
        description="The subscription checkout flow designed to remain simple for learners while handling significant backend complexity."
        imageUrl={images.babbelCheckout}
        imageAlt="Babbel checkout"
        layout="full"
      />
      <VisualShowcase
        layout="full"
        title="In-App Learning Experience"
        description="A selection of Babbel app screens illustrating key learner interactions: setting daily reminders, completing listening and speaking exercises, and practicing typing skills. These screens demonstrate how the subscription and payment systems support a seamless learning journey, enabling users to engage consistently without friction."
        imageUrl={images.babbel7}
        imageAlt="In-app screens"
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
      />
      <KeyFeatures
        title="Subscription Payments in Practice"
        description="Features designed to balance customer simplicity with financial correctness and scale."
        features={[
          {
            icon: CreditCard,
            title: "Stripe-powered payment processing",
            description:
              "Secure handling of payments and subscriptions using Stripe as the external system of record.",
          },
          {
            icon: Repeat,
            title: "Subscription lifecycle management",
            description:
              "Supports renewals, upgrades, cancellations, retries, and failure states without breaking customer access.",
          },
          {
            icon: Globe,
            title: "Global-ready billing",
            description:
              "Handles multiple currencies and regional tax requirements as Babbel scales internationally.",
          },
          {
            icon: ShieldCheck,
            title: "Defensive failure handling",
            description:
              "Designed to degrade safely when payment issues occur, protecting both revenue and user trust.",
          },
          {
            icon: Activity,
            title: "Operational visibility",
            description:
              "Clear tracing and observability across payment and subscription events to support rapid diagnosis.",
          },
          {
            icon: Layers,
            title: "Extensible architecture",
            description:
              "Provides a foundation for evolving subscription offerings without introducing payment risk.",
          },
        ]}
      />
      <ProcessSection
        title="Engineering a Reliable Subscription Platform"
        description="A systems-focused approach balancing product needs, financial correctness, and long-term maintainability."
        phases={[
          {
            number: "01",
            title: "Domain Understanding & Constraints",
            description:
              "Mapped the subscription and payments domain, identifying regulatory, financial, and operational constraints alongside product requirements.",
            deliverables: [
              "Subscription lifecycle mapping",
              "Risk and failure mode analysis",
              "Stakeholder alignment",
              "Technical constraints documentation",
            ],
          },
          {
            number: "02",
            title: "Architecture & Integration Design",
            description:
              "Defined clear boundaries between Stripe, checkout services, and internal entitlement systems to reduce coupling and failure propagation.",
            deliverables: [
              "Integration architecture",
              "Event and webhook handling strategy",
              "Idempotency and consistency guarantees",
              "System boundaries documentation",
            ],
          },
          {
            number: "03",
            title: "Implementation & Hardening",
            description:
              "Built and refined payment flows with a focus on correctness, retries, and defensive handling of edge cases.",
            deliverables: [
              "Checkout and subscription flows",
              "Failure and retry handling",
              "Logging and monitoring",
              "Internal documentation",
            ],
          },
          {
            number: "04",
            title: "Validation & Operational Readiness",
            description:
              "Validated flows through testing and real-world usage, ensuring teams could safely operate and extend the system.",
            deliverables: [
              "End-to-end testing",
              "Operational runbooks",
              "Monitoring dashboards",
              "Post-launch improvements",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="This work strengthened Babbel’s subscription and payments foundation, enabling the platform to scale globally while maintaining a smooth, trustworthy checkout experience. By prioritising correctness, clear system boundaries, and operational visibility, the project reduced risk in a critical revenue domain and allowed product teams to evolve subscription offerings with confidence."
        impactLabel="Project Impact"
        impactTitle="Reliable payments infrastructure that scales with the business"
        nextProject={{
          title: "Emerging Industries",
          link: "/project/emerging-industries",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Building trust and reliability at scale",
          description:
            "Integrating Babbel’s subscription payments system reinforced that engineering and design are inseparable in high-stakes domains. Success isn’t just about connecting Stripe or shipping checkout screens — it’s about orchestrating a global, distributed system that reliably handles multiple currencies, payment methods, and regulatory requirements while keeping the learner experience seamless. Thoughtful architecture and design enable users to focus on learning, not payments, and give product teams confidence to scale globally without compromise.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Babbel };
